import type {
	IntakeCaptureResult,
	IntakeConfirmationResult,
	IntakeContextResult,
	IntakeKind,
	IntakeSeverity
} from "~/services/aiOrchestrator";

import logger from "~/helpers/logger";

export interface IntakeDraft {
	captureId: string;
	context: IntakeContextResult;
	description: string;
	intakeKind: IntakeKind;
	resolution?: IntakeResolution;
	severity?: IntakeSeverity;
	title: string;
}

export type IntakeResolutionState = "DECLINED" | "SUBMITTED";

export interface IntakeResolution {
	description: string;
	intakeKind: IntakeKind;
	message?: string;
	referenceId?: string;
	severity?: IntakeSeverity;
	state: IntakeResolutionState;
	title: string;
}

const INTAKE_KINDS: IntakeKind[] = ["BUG", "FEATURE_REQUEST"];

const KIND_LABELS: Record<IntakeKind, string> = {
	BUG: "Bug",
	FEATURE_REQUEST: "Feature request"
};

const KIND_NOUNS: Record<IntakeKind, string> = {
	BUG: "bug report",
	FEATURE_REQUEST: "feature request"
};

const SEVERITY_LABELS: Record<IntakeSeverity, string> = {
	BLOCKING: "Blocking",
	CRITICAL: "Critical",
	HIGH: "High",
	LOW: "Low",
	MEDIUM: "Medium"
};

const intakeKindLabel = (kind: IntakeKind): string => KIND_LABELS[kind];

const intakeKindNoun = (kind: IntakeKind): string => KIND_NOUNS[kind];

const severityLabel = (severity: IntakeSeverity): string => SEVERITY_LABELS[severity];

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

const isIntakeKind = (value: unknown): value is IntakeKind => INTAKE_KINDS.includes(value as IntakeKind);

const isIntakeContext = (value: unknown): value is IntakeContextResult =>
	typeof value === "object" &&
	value !== null &&
	typeof (value as IntakeContextResult).pageLabel === "string" &&
	typeof (value as IntakeContextResult).appVersion === "string" &&
	typeof (value as IntakeContextResult).tenantName === "string" &&
	typeof (value as IntakeContextResult).userName === "string";

const isIntakeCaptureResult = (value: unknown): value is IntakeCaptureResult =>
	typeof value === "object" &&
	value !== null &&
	isNonEmptyString((value as IntakeCaptureResult).captureId) &&
	isIntakeKind((value as IntakeCaptureResult).intakeKind) &&
	typeof (value as IntakeCaptureResult).title === "string" &&
	typeof (value as IntakeCaptureResult).description === "string" &&
	isIntakeContext((value as IntakeCaptureResult).context);

const toIntakeDraft = (capture: IntakeCaptureResult): IntakeDraft => ({
	captureId: capture.captureId,
	context: capture.context,
	description: capture.description,
	intakeKind: capture.intakeKind,
	severity: capture.intakeKind === "BUG" ? capture.severity : undefined,
	title: capture.title
});

const buildIntakeDrafts = (captures: IntakeCaptureResult[]): IntakeDraft[] => {
	const drafts: IntakeDraft[] = [];
	for (const capture of captures) {
		if (!isIntakeCaptureResult(capture)) {
			logger.warn("Malformed Fernie intake capture", { capture });
			continue;
		}
		drafts.push(toIntakeDraft(capture));
	}
	return drafts;
};

const isSubmittedIntake = (intake?: IntakeConfirmationResult | null): intake is IntakeConfirmationResult =>
	intake != null && intake.status === "SUBMITTED" && isNonEmptyString(intake.referenceId);

const toTrimmedMessage = (value?: string): string | undefined => (isNonEmptyString(value) ? value.trim() : undefined);

interface DeclineFields {
	description: string;
	intakeKind: IntakeKind;
	message?: string;
	severity?: IntakeSeverity;
	title: string;
}

const buildDeclineResolution = ({
	description,
	intakeKind,
	message,
	severity,
	title
}: DeclineFields): IntakeResolution => ({
	description,
	intakeKind,
	message: toTrimmedMessage(message),
	severity,
	state: "DECLINED",
	title
});

const resolveIntakeSubmission = (intake: IntakeConfirmationResult, draft: IntakeDraft): IntakeResolution => ({
	description: draft.description,
	intakeKind: intake.kind,
	referenceId: intake.referenceId,
	severity: draft.severity,
	state: "SUBMITTED",
	title: intake.title
});

const declineResolution = (draft: IntakeDraft, message?: string): IntakeResolution =>
	buildDeclineResolution({
		description: draft.description,
		intakeKind: draft.intakeKind,
		message,
		severity: draft.severity,
		title: draft.title
	});

export {
	buildIntakeDrafts,
	declineResolution,
	intakeKindLabel,
	intakeKindNoun,
	isIntakeCaptureResult,
	isSubmittedIntake,
	resolveIntakeSubmission,
	severityLabel
};
export default buildIntakeDrafts;
